import { tetrominoes } from "./utilities";
import { Howl } from "howler";

// Import celebration sounds for winner effects
import clapsSoundPath from "../assets/sounds/claps.wav";
import bonusSoundPath from "../assets/sounds/bonus.wav";
import niceSoundPath from "../assets/sounds/nice.mp3";
import wowSoundPath from "../assets/sounds/wow.mp3";

// Local deep clone function - handles both arrays and objects
const deepCloneArray = (input) => {
  if (Array.isArray(input)) {
    return input.map((element) =>
      Array.isArray(element) ? deepCloneArray(element) : element
    );
  } else if (input && typeof input === "object") {
    // Clone objects
    const cloned = {};
    for (const key in input) {
      if (input.hasOwnProperty(key)) {
        cloned[key] = Array.isArray(input[key])
          ? deepCloneArray(input[key])
          : input[key];
      }
    }
    return cloned;
  }
  return input;
};

// Local board matrix creation - correct version without the utilities.js bug
const createOnlineBoardMatrix = (boardRows, boardColumns) => {
  const playerBoardMatrix = [];
  for (let i = 0; i < boardRows; i++) {
    playerBoardMatrix[i] = [];
    for (let j = 0; j < boardColumns; j++) {
      playerBoardMatrix[i].push(0);
    }
  }
  return playerBoardMatrix;
};

// OpponentSimulator class - simulates opponent game state locally for responsive input processing
class OpponentSimulator {
  constructor(opponentId, boardRows = 20, boardColumns = 10, onStateUpdate = null) {
    this.opponentId = opponentId;
    this.boardRows = boardRows;
    this.boardColumns = boardColumns;
    this.onStateUpdate = onStateUpdate;
    
    // Opponent's simulated game state
    this.state = {
      boardMatrix: createOnlineBoardMatrix(boardRows, boardColumns),
      boardVersion: 0, // Increments when board changes for React re-renders
      currentTetromino: null,
      nextTetromino: null,
      hardDropPreview: null,
      score: 0,
      level: 1,
      lines: 0,
      isGameOver: false,
      destroyInAction: false,
      lastUpdate: Date.now(),
      singleLines: 0,
      doubleLines: 0,
      tripleLines: 0,
      tetrisLines: 0
    };
    
    // Track processed inputs to prevent duplicates
    this.processedInputs = new Set();

    // Track laser animation
    this.laserAnimationId = null;

    // 3D Effect event listeners (same interface as OnlineGame for TetrisBoard3D compatibility)
    this.effectListeners = new Map();
  }

  // Event system for 3D effects (same interface as OnlineGame)
  onEffect(eventName, callback) {
    if (!this.effectListeners.has(eventName)) {
      this.effectListeners.set(eventName, []);
    }
    this.effectListeners.get(eventName).push(callback);
    return () => {
      const listeners = this.effectListeners.get(eventName);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) listeners.splice(index, 1);
      }
    };
  }

  emitEffect(eventName, data) {
    const listeners = this.effectListeners.get(eventName);
    if (listeners) listeners.forEach(cb => cb(data));
  }

  // Capture block positions and colors from rows for 3D effects
  captureBlocksFromRows(rows) {
    const blocks = [];
    rows.forEach(row => {
      for (let col = 0; col < this.boardColumns; col++) {
        const cell = this.state.boardMatrix[row][col];
        if (cell && cell !== 0) {
          blocks.push({ row, col, colorClass: cell });
        }
      }
    });
    return blocks;
  }

  // Process opponent input locally 
  processInput(action, inputId, timestamp) {
    // Prevent duplicate processing
    if (this.processedInputs.has(inputId)) {
      return false;
    }
    this.processedInputs.add(inputId);

    // Keep processed inputs manageable (last 50)
    if (this.processedInputs.size > 50) {
      const idsArray = Array.from(this.processedInputs);
      this.processedInputs.clear();
      idsArray.slice(-25).forEach(id => this.processedInputs.add(id));
    }

    // Don't process if opponent is game over or in destroy action
    if (this.state.isGameOver || this.state.destroyInAction) {
      return false;
    }


    // Process the input action
    switch (action) {
      case "left":
        return this.movePiece("left");
      case "right":
        return this.movePiece("right");  
      case "down":
        return this.movePiece("down");
      case "gravity_down":
        return this.movePiece("down"); // Gravity drop for opponent
      case "rotate":
        return this.rotatePiece();
      case "hard_drop":
        return this.hardDrop();
      case "piece_spawn":
        // Handle piece spawning for opponents - don't generate random, use provided data
        return true; // Always successful, actual spawn data will come from network
      case "lifeSaver":
        return this.useLifeSaver();
      default:
        return false;
    }
  }

  // Simulate piece movement for opponent
  movePiece(direction) {
    if (!this.state.currentTetromino) return false;

    const newTetromino = deepCloneArray(this.state.currentTetromino);

    switch (direction) {
      case "left":
        newTetromino.allCoordinates.forEach((coord) => coord[1]--);
        break;
      case "right":
        newTetromino.allCoordinates.forEach((coord) => coord[1]++);
        break;
      case "down":
        newTetromino.allCoordinates.forEach((coord) => coord[0]++);
        break;
    }

    if (!this.checkCollision(newTetromino)) {
      this.state.currentTetromino = newTetromino;
      this.updateHardDropPreview();
      return true;
    } else if (direction === "down") {
      // Piece landed - place it
      this.placePiece();
      return true;
    }
    
    return false;
  }

  // Simulate piece rotation for opponent
  rotatePiece() {
    if (!this.state.currentTetromino) return false;

    const newTetromino = deepCloneArray(this.state.currentTetromino);
    const center = newTetromino.allCoordinates[0]; // Use first piece as rotation center

    // Rotate around center
    newTetromino.allCoordinates.forEach((coord) => {
      const x = coord[0] - center[0];
      const y = coord[1] - center[1];
      coord[0] = center[0] - y;
      coord[1] = center[1] + x;
    });

    if (!this.checkCollision(newTetromino)) {
      this.state.currentTetromino = newTetromino;
      this.updateHardDropPreview();
      return true;
    }
    
    return false;
  }

  // Simulate hard drop for opponent
  hardDrop() {
    if (!this.state.currentTetromino || !this.state.hardDropPreview) {
      return false;
    }

    // Emit hard drop effect for 3D skin (before moving piece)
    const startY = Math.min(...this.state.currentTetromino.allCoordinates.map(c => c[0]));
    const endY = Math.min(...this.state.hardDropPreview.allCoordinates.map(c => c[0]));
    const columns = [...new Set(this.state.currentTetromino.allCoordinates.map(c => c[1]))];
    this.emitEffect('hardDrop', {
      startY: 20 - startY,
      endY: 20 - endY,
      columns,
      colorClass: this.state.currentTetromino.colorClass
    });

    // Move piece to hard drop position using preview coordinates
    this.state.currentTetromino = {
      ...this.state.currentTetromino,
      allCoordinates: deepCloneArray(this.state.hardDropPreview.allCoordinates)
    };
    
    // Clear the hard drop preview since piece is now placed
    this.state.hardDropPreview = null;
    
    // Place piece and check for line clears
    this.placePiece();
    return true;
  }

  // Check collision (same logic as main game)
  checkCollision(tetromino) {
    if (!tetromino) return true;

    return tetromino.allCoordinates.some(([row, col]) => {
      // Check boundaries
      if (row < 0 || row >= this.boardRows || col < 0 || col >= this.boardColumns) {
        return true;
      }
      // Check collision with placed pieces
      return this.state.boardMatrix[row] && this.state.boardMatrix[row][col] !== 0;
    });
  }

  // Update hard drop preview (same logic as main game)
  updateHardDropPreview() {
    if (!this.state.currentTetromino) {
      this.state.hardDropPreview = null;
      return;
    }

    let currentTetromino = deepCloneArray(this.state.currentTetromino);
    let flag = false;

    // Keep dropping until collision
    while (!this.checkCollision(currentTetromino)) {
      currentTetromino.allCoordinates.forEach((coord) => coord[0]++);
      flag = true;
    }

    if (flag) {
      // Move back one step to last valid position
      currentTetromino.allCoordinates.forEach((coord) => coord[0]--);
      this.state.hardDropPreview = {
        allCoordinates: deepCloneArray(currentTetromino.allCoordinates),
        colorClass: "hard-drop-active",
      };
    } else {
      this.state.hardDropPreview = null;
    }
  }

  // Place piece on board (simplified version)
  placePiece() {
    if (!this.state.currentTetromino) return;

    // Calculate center position for block place effect
    const coords = this.state.currentTetromino.allCoordinates;
    const avgRow = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
    const avgCol = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;

    // Emit block place effect for 3D skin
    this.emitEffect('blockPlace', {
      position: [avgCol - 5 + 0.5, 20 - avgRow - 0.5, 0],
      colorClass: this.state.currentTetromino.colorClass
    });

    // Place piece on board
    this.state.currentTetromino.allCoordinates.forEach(([row, col]) => {
      if (row >= 0 && row < this.boardRows && col >= 0 && col < this.boardColumns) {
        this.state.boardMatrix[row][col] = this.state.currentTetromino.colorClass;
      }
    });

    // Increment board version for React re-rendering
    this.state.boardVersion++;

    // Check for completed lines and clear them
    const completedLines = this.checkCompletedLines();
    if (completedLines.length > 0) {
      this.clearLines(completedLines);
    }

    // Clear current piece (will be updated by network events)
    this.state.currentTetromino = null;
    this.state.hardDropPreview = null;
    this.state.lastUpdate = Date.now();
  }

  // Check for completed lines
  checkCompletedLines() {
    const completedLines = [];
    for (let row = 0; row < this.boardRows; row++) {
      if (this.state.boardMatrix[row].every((cell) => cell !== 0)) {
        completedLines.push(row);
      }
    }
    return completedLines;
  }

  // Clear completed lines with laser animation
  clearLines(lines) {
    if (lines.length === 0) return;

    // Capture block colors before clearing for 3D effects
    const blocksData = this.captureBlocksFromRows(lines);

    // Emit line clear or tetris effect for 3D skin
    if (lines.length >= 2) {
      // 2+ lines - Emit special finisher effect with block data
      this.emitEffect('tetris', { rows: lines, linesCleared: lines.length, blocks: blocksData });
    } else {
      this.emitEffect('lineClear', { rows: lines, linesCleared: lines.length, blocks: blocksData });
    }

    // Emit score animation effect for 3D rendering (skip for default board)
    if (!this.themeSounds?.currentThemeConfig?.disableScoreAnimation) {
      this.emitEffect('scoreAnimation', {
        rows: lines.length,
        score: lines.length * 100 * lines.length,
        colorClass: blocksData[0]?.colorClass,
      });
    }

    // Start laser animation for opponents (2D mode)
    this.startLaserBeamAnimation(lines);
  }

  // Laser beam animation for opponents
  startLaserBeamAnimation(destroyableRows) {
    let column = 0;
    const msPerStep = 22.33; // Milliseconds per step (3x speed - 67/3)
    let lastStepTime = performance.now();
    this.state.destroyInAction = true;

    const laserAnimationLoop = (currentTime) => {
      const elapsed = currentTime - lastStepTime;

      if (elapsed >= msPerStep) {
        if (column >= this.boardColumns) {
          // Animation complete - finish clearing lines
          this.laserAnimationId = null;
          this.finishLineClear(destroyableRows);
          return;
        }

        // Update laser beam visual effect for opponent
        destroyableRows.forEach((row) => {
          this.state.boardMatrix[row][column] = "";

          // Update opponent's laser beam width if DOM element exists
          const laserBeam = document.querySelector(
            `#opponent-${this.opponentId}-laser-beam-${row}`
          );
          if (laserBeam) {
            // Show and animate the laser beam
            laserBeam.style.width = (26 * (column + 1)) + "px";
          } else {
          }
        });

        // Trigger state update for real-time rendering during animation
        this.state.lastUpdate = Date.now();
        // Increment board version for 3D board re-rendering
        this.state.boardVersion++;

        // Notify main game of state change for immediate UI update
        if (this.onStateUpdate) {
          this.onStateUpdate(this.opponentId, this.getState());
        }

        column++;
        lastStepTime = currentTime;
      }

      this.laserAnimationId = requestAnimationFrame(laserAnimationLoop);
    };

    this.laserAnimationId = requestAnimationFrame(laserAnimationLoop);
  }

  // Finish line clearing for opponents
  finishLineClear(destroyableRows) {
    // Reset laser beams
    destroyableRows.forEach((row) => {
      const laserBeam = document.querySelector(
        `#opponent-${this.opponentId}-laser-beam-${row}`
      );
      if (laserBeam) {
        // Hide the laser beam and reset width
        laserBeam.style.width = "0px";
      } else {
      }
    });

    // Remove completed lines
    destroyableRows.forEach((lineIndex) => {
      this.state.boardMatrix.splice(lineIndex, 1);
      // Add new empty line at top
      this.state.boardMatrix.unshift(new Array(this.boardColumns).fill(0));
    });

    // Update stats (basic scoring)
    const lineCount = destroyableRows.length;
    this.state.lines += lineCount;

    // Track line clearing breakdown for opponents too
    switch (lineCount) {
      case 1:
        this.state.singleLines++;
        break;
      case 2:
        this.state.doubleLines++;
        break;
      case 3:
        this.state.tripleLines++;
        break;
      case 4:
        this.state.tetrisLines++;
        break;
    }

    const scoreMultiplier = [0, 100, 400, 900, 1600][lineCount] || 0;
    this.state.score += scoreMultiplier * this.state.level;

    // Level progression disabled - keep at level 1 for consistent scoring
    // const newLevel = Math.floor(this.state.lines / 10) + 1;
    // if (newLevel > this.state.level) {
    //   this.state.level = newLevel;
    // }

    // Clear destroy action flag
    this.state.destroyInAction = false;
    this.state.lastUpdate = Date.now();
    // Increment board version for final re-render after line clear completes
    this.state.boardVersion++;
  }

  // Sync state from network (for validation and piece updates)
  syncFromNetwork(networkData) {
    // Update state from network data, but don't overwrite local simulation
    if (networkData.board) {
      // Don't sync board if we're in the middle of a laser animation
      // This prevents network updates from overwriting the partially-cleared board
      if (!this.state.destroyInAction) {
        // Only sync board if significantly different (to avoid overriding local simulation)
        // For now, always accept network board state for simplicity
        this.state.boardMatrix = networkData.board;
        // Increment board version to trigger React re-render
        this.state.boardVersion++;
      }
    }

    if (networkData.currentPiece) {
      this.state.currentTetromino = networkData.currentPiece;
      this.updateHardDropPreview();
    }

    if (networkData.nextPiece) {
      this.state.nextTetromino = networkData.nextPiece;
    }

    // Always accept authoritative score/level data
    if (networkData.score !== undefined) this.state.score = networkData.score;
    if (networkData.level !== undefined) this.state.level = networkData.level;
    if (networkData.lines !== undefined) this.state.lines = networkData.lines;
    if (networkData.isGameOver !== undefined) this.state.isGameOver = networkData.isGameOver;

    // Sync line clear breakdown stats
    if (networkData.singleLines !== undefined) this.state.singleLines = networkData.singleLines;
    if (networkData.doubleLines !== undefined) this.state.doubleLines = networkData.doubleLines;
    if (networkData.tripleLines !== undefined) this.state.tripleLines = networkData.tripleLines;
    if (networkData.tetrisLines !== undefined) this.state.tetrisLines = networkData.tetrisLines;

    this.state.lastUpdate = Date.now();
  }

  // Get current state for rendering
  getState() {
    return { ...this.state };
  }

  // Life saver laser animation for opponents (similar to line clear but different completion)
  startLifeSaverAnimation(destroyableRows) {
    // Capture block colors before clearing for 3D effects
    const blocksData = this.captureBlocksFromRows(destroyableRows);

    // Emit lineClear effect for 3D skin (life saver fires line clear, not finisher)
    this.emitEffect('lineClear', {
      rows: destroyableRows,
      linesCleared: destroyableRows.length,
      blocks: blocksData,
      isLifeSaver: true
    });

    let column = 0;
    const msPerStep = 22.33; // Milliseconds per step (3x speed - 67/3)
    let lastStepTime = performance.now();
    this.state.destroyInAction = true;

    const laserAnimationLoop = (currentTime) => {
      const elapsed = currentTime - lastStepTime;

      if (elapsed >= msPerStep) {
        if (column >= this.boardColumns) {
          // Animation complete - finish life saver operation
          this.laserAnimationId = null;
          this.finishLifeSaver(destroyableRows);
          return;
        }

        // Update laser beam visual effect for opponent
        destroyableRows.forEach((row) => {
          this.state.boardMatrix[row][column] = "";

          // Update opponent's laser beam width if DOM element exists
          const laserBeam = document.querySelector(
            `#opponent-${this.opponentId}-laser-beam-${row}`
          );
          if (laserBeam) {
            // Show and animate the laser beam
            laserBeam.style.width = (26 * (column + 1)) + "px";
          }
        });

        // Trigger state update for real-time rendering during animation
        this.state.lastUpdate = Date.now();
        // Increment board version for 3D board re-rendering
        this.state.boardVersion++;

        // Notify main game of state change for immediate UI update
        if (this.onStateUpdate) {
          this.onStateUpdate(this.opponentId, this.getState());
        }

        column++;
        lastStepTime = currentTime;
      }

      this.laserAnimationId = requestAnimationFrame(laserAnimationLoop);
    };

    this.laserAnimationId = requestAnimationFrame(laserAnimationLoop);
  }

  // Finish life saver animation for opponents
  finishLifeSaver(destroyableRows) {
    // Reset laser beams
    destroyableRows.forEach((row) => {
      const laserBeam = document.querySelector(
        `#opponent-${this.opponentId}-laser-beam-${row}`
      );
      if (laserBeam) {
        // Hide the laser beam and reset width
        laserBeam.style.width = "0px";
      }
    });

    // Clear the rows (set all cells to 0)
    destroyableRows.forEach(row => {
      for (let col = 0; col < this.boardColumns; col++) {
        this.state.boardMatrix[row][col] = 0;
      }
    });

    // Shift blocks down using existing method
    this.shiftBlocksDown(destroyableRows);

    // Increment board version for React re-rendering
    this.state.boardVersion++;

    // Clear destroy action flag
    this.state.destroyInAction = false;
    this.state.lastUpdate = Date.now();
  }

  // Shift blocks down for life saver (similar to line clear)
  shiftBlocksDown(destroyableRows) {
    // Sort rows in descending order to process from bottom to top
    const sortedRows = [...destroyableRows].sort((a, b) => b - a);

    sortedRows.forEach(row => {
      // Move all rows above this one down by one position
      for (let r = row; r > 0; r--) {
        for (let c = 0; c < this.boardColumns; c++) {
          this.state.boardMatrix[r][c] = this.state.boardMatrix[r - 1][c];
        }
      }
      // Clear the top row
      for (let c = 0; c < this.boardColumns; c++) {
        this.state.boardMatrix[0][c] = 0;
      }
    });
  }

  // Life saver for opponent simulator
  useLifeSaver() {
    // Note: lifeSaverCount is not tracked in opponent simulator
    // The visual effect is what matters for opponents
    if (this.state.destroyInAction || this.state.isGameOver) {
      return false;
    }

    // Find first non-empty row from top
    let firstNonEmptyRow = -1;
    for (let row = 0; row < this.boardRows; row++) {
      // Check if any cell in this row is filled
      if (this.state.boardMatrix[row].some(cell => cell !== 0)) {
        firstNonEmptyRow = row;
        break;
      }
    }

    if (firstNonEmptyRow !== -1) {
      // Create array of rows to destroy (top 5 rows starting from first non-empty)
      const destroyableRows = [firstNonEmptyRow];
      let nextRow = firstNonEmptyRow + 1;
      while (destroyableRows.length < 5 && nextRow < this.boardRows) {
        destroyableRows.push(nextRow);
        nextRow++;
      }

      // Start laser animation for opponent life saver
      this.startLifeSaverAnimation(destroyableRows);
      return true;
    }
    return false;
  }

  // Cleanup method for opponent simulator
  cleanup() {
    // Cancel any running laser beam animation
    if (this.laserAnimationId) {
      cancelAnimationFrame(this.laserAnimationId);
      this.laserAnimationId = null;
    }
    
    // Clear destroy action flag
    this.state.destroyInAction = false;
  }
}

export class OnlineGame {
  constructor(playerId, onGameEvent, sounds = null, gameSettings = {}, onOpponentStateChange = null) {
    this.playerId = playerId;
    this.onGameEvent = onGameEvent;
    this.sounds = sounds;
    this.onOpponentStateChange = onOpponentStateChange; // Callback for opponent state changes during animations

    // Add celebration sounds for winner effects
    if (this.sounds) {
      this.sounds.clapsSound = new Howl({
        src: [clapsSoundPath],
      });

      this.sounds.bonusSound = new Howl({
        src: [bonusSoundPath],
      });

      this.sounds.niceSound = new Howl({
        src: [niceSoundPath],
      });

      this.sounds.wowSound = new Howl({
        src: [wowSoundPath],
      });
    }

    // Game settings
    this.gameSettings = {
      gameMode: "survival",
      timeLimit: 600,
      winCondition: "last_survivor",
      speedMultipliers: {
        0: 3.0,   // 3x at start
        60: 3.5,  // 3.5x at 1 minute
        120: 4.0, // 4x at 2 minutes
        180: 4.5, // 4.5x at 3 minutes
        240: 5.0, // 5x at 4 minutes
        300: 5.5, // 5.5x at 5 minutes
        360: 6.0, // 6x at 6 minutes
        420: 6.5, // 6.5x at 7 minutes
        480: 7.0, // 7x at 8 minutes
        540: 7.5, // 7.5x at 9 minutes
        600: 8.0, // 8x at 10 minutes (max speed)
      },
      ...gameSettings,
    };

    // Game state
    this.isGameStarted = false;
    this.isGameOver = false;
    this.isPaused = false;
    
    // Animation IDs for cleanup
    this.gameLoopId = null;
    this.laserAnimationId = null;

    // Board configuration
    this.boardRows = 20;
    this.boardColumns = 10;
    this.speed = 30; // Initial speed
    this.baseSpeed = 30; // Store original speed for scaling

    // Local player state
    this.player = {
      id: playerId,
      boardMatrix: createOnlineBoardMatrix(this.boardRows, this.boardColumns),
      boardVersion: 0, // Increments when board changes for React re-renders
      currentTetromino: null,
      nextTetromino: null,
      hardDropCoordinates: null,
      hardDropPreview: null, // For visual preview
      destroyInAction: false, // For laser beam animation
      score: 0,
      level: 1,
      lines: 0,
      isGameOver: false,
      lifeSaverCount: 3, // Life saver feature
      stats: {
        score: 0,
        level: 1,
        linesCleared: 0,
        gameTime: 0,
        piecesPlaced: 0,
        singleLines: 0, // 1x line clears
        doubleLines: 0, // 2x line clears
        tripleLines: 0, // 3x line clears
        tetrisLines: 0, // 4x line clears
      },
    };

    // Game timing
    this.gameStartTime = null;
    this.gameTime = 0; // Current game time in seconds
    this.lastGameTimeUpdate = 0; // For tracking game time

    // Game mode specific
    this.isTimeUp = false;
    this.lastSpeedUpdate = -1; // Track when speed was last updated (start at -1 to allow 0 threshold)
    this.currentSpeedMultiplier = 1.0;

    // Opponents state (updated from network events)
    this.opponents = new Map();
    
    // New: Local opponent simulators for input processing
    this.opponentSimulators = new Map(); // Map<opponentId, OpponentSimulator>

    // Game timing
    this.lastDropTime = 0;
    this.dropInterval = 800; // milliseconds between automatic drops (a bit faster)
    this.lastLogTime = 0; // For throttling console logs
    this.lastCollisionLogTime = 0; // For throttling collision logs
    this.gameLoopId = null;
    this.isRunning = false;

    // New input-based system
    this.inputSequenceId = 0; // To track input order
    this.processedInputIds = new Set(); // To prevent double processing
    this.lastStateSync = 0; // For periodic state sync
    this.stateSyncInterval = 3000; // Sync every 3 seconds

    // Frame rate tracking for InGameStats compatibility
    this.fixedFrameRate = {
      currentFps: 60,
      averageFps: 60,
      lastRan: -1,
    };

    // Tetromino management
    this.tetrominoQueue = [];
    this.randomTetrominoIndexes = this.generateRandomTetrominoSequence();
    this.currentTetrominoIndex = 0;

    // 3D Effect event listeners (for Prime skin effects)
    this.effectListeners = new Map();

    this.initializeGame();
  }

  // Event system for 3D effects
  onEffect(eventName, callback) {
    if (!this.effectListeners.has(eventName)) {
      this.effectListeners.set(eventName, []);
    }
    this.effectListeners.get(eventName).push(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.effectListeners.get(eventName);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  emitEffect(eventName, data) {
    const listeners = this.effectListeners.get(eventName);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  initializeGame() {
    // Generate initial tetromino queue
    this.fillTetrominoQueue();
    this.spawnNextTetromino();
  }

  generateRandomTetrominoSequence() {
    // Generate a shuffled sequence of tetromino indices (0-4 for the 5 tetromino types)
    const indices = [0, 1, 2, 3, 4];
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }

  fillTetrominoQueue() {
    while (this.tetrominoQueue.length < 3) {
      if (this.currentTetrominoIndex >= this.randomTetrominoIndexes.length) {
        this.randomTetrominoIndexes = this.generateRandomTetrominoSequence();
        this.currentTetrominoIndex = 0;
      }

      const tetrominoIndex =
        this.randomTetrominoIndexes[this.currentTetrominoIndex];
      const tetromino = this.createTetromino(tetrominoIndex);
      this.tetrominoQueue.push(tetromino);
      this.currentTetrominoIndex++;
    }
  }

  createTetromino(index) {
    const tetrominoList = tetrominoes(this.boardColumns);
    const tetrominoData = tetrominoList[index];

    if (!tetrominoData) {
      // Return a default tetromino if index is invalid
      return this.createTetromino(0);
    }

    // Create tetromino at normal spawn position (top of visible board)
    const coordinates = deepCloneArray(tetrominoData.allCoordinates);

    // Keep original coordinates - pieces spawn at row 0 (top of visible board)
    // No need to move pieces above the board

    return {
      allCoordinates: coordinates,
      colorClass: tetrominoData.colorClass,
      tetrominoIndex: index,
    };
  }

  spawnNextTetromino() {
    if (this.tetrominoQueue.length === 0) {
      this.fillTetrominoQueue();
    }

    const newCurrentPiece = this.tetrominoQueue.shift();
    const newNextPiece = this.tetrominoQueue[0] || null;

    // Send piece spawn event to sync with opponents (only when game is started, not during initialization)
    if (newCurrentPiece && this.isGameStarted) {
      const inputId = `${this.playerId}_spawn_${++this.inputSequenceId}_${Date.now()}`;
      this.sendInputEvent("piece_spawn", inputId, {
        currentPiece: {
          colorClass: newCurrentPiece.colorClass,
          allCoordinates: newCurrentPiece.allCoordinates
        },
        nextPiece: newNextPiece ? {
          colorClass: newNextPiece.colorClass,
          allCoordinates: newNextPiece.allCoordinates
        } : null
      });
    }

    this.player.currentTetromino = newCurrentPiece;
    this.player.nextTetromino = newNextPiece;

    this.fillTetrominoQueue();

    // Debug: Check spawning coordinates

    // Check if game over (new piece can't spawn)
    if (this.checkCollision(this.player.currentTetromino)) {
      this.gameOver();
      return;
    }

    this.updateHardDropPreview();

    // New system: No need to send heavy piece_update events
    // State sync will handle periodic synchronization
  }

  start() {
    if (this.isGameStarted) return;

    this.isGameStarted = true;
    this.isRunning = true;
    this.lastDropTime = Date.now();
    this.gameStartTime = Date.now();
    this.lastGameTimeUpdate = Date.now();
    this.gameTime = 0;

    // Reset game mode specific properties
    this.isTimeUp = false;
    this.currentSpeedMultiplier = 1.0;
    this.lastSpeedUpdate = -1;

    // Start game loop with requestAnimationFrame
    const gameLoop = () => {
      if (this.isRunning) {
        this.gameLoop();
        this.gameLoopId = requestAnimationFrame(gameLoop);
      }
    };
    this.gameLoopId = requestAnimationFrame(gameLoop);

  }

  gameLoop() {
    if (!this.isGameStarted || this.isPaused || this.player.isGameOver || this.player.destroyInAction) {
      return;
    }

    const now = Date.now();

    // Update game time
    if (now - this.lastGameTimeUpdate >= 1000) {
      this.gameTime = Math.floor((now - this.gameStartTime) / 1000);
      this.lastGameTimeUpdate = now;

      // Check game mode specific conditions
      this.checkGameModeConditions();
    }

    // Periodic state sync (new system)
    if (now - this.lastStateSync >= this.stateSyncInterval) {
      this.sendStateSync();
      this.lastStateSync = now;
    }

    // Simple FPS tracking
    if (this.fixedFrameRate.lastRan !== -1) {
      const deltaTime = now - this.fixedFrameRate.lastRan;
      if (deltaTime > 0) {
        this.fixedFrameRate.currentFps = Math.round(1000 / deltaTime);
      }
    }
    this.fixedFrameRate.lastRan = now;

    // Apply speed scaling based on current multiplier
    const adjustedDropInterval =
      this.dropInterval / this.currentSpeedMultiplier;

    // Automatic piece dropping
    if (now - this.lastDropTime >= adjustedDropInterval) {
      if (this.player.currentTetromino) {
        const currentBottomRow = Math.max(
          ...this.player.currentTetromino.allCoordinates.map((c) => c[0])
        );
        // Reduced logging to prevent spam
        if (Date.now() - this.lastLogTime > 5000) { // Log max once per 5 seconds
          this.lastLogTime = Date.now();
        }
        
        // Send gravity input event to opponents so they can simulate the same drop
        const inputId = `${this.playerId}_gravity_${++this.inputSequenceId}_${Date.now()}`;
        this.sendInputEvent("gravity_down", inputId);
        
        // Process locally (automatic gravity, no sound)
        this.processInput("gravity_down", inputId, true); // true = isLocal
      }
      this.lastDropTime = now;
    }
  }

  checkGameModeConditions() {
    // Check time limit for time-based modes
    if (
      this.gameSettings.timeLimit &&
      this.gameTime >= this.gameSettings.timeLimit
    ) {
      if (!this.isTimeUp) {
        this.isTimeUp = true;
        this.handleTimeUp();
      }
      return;
    }

    // Check speed scaling
    if (
      this.gameSettings.speedMultipliers &&
      Object.keys(this.gameSettings.speedMultipliers).length > 0
    ) {
      for (const [timeThreshold, multiplier] of Object.entries(
        this.gameSettings.speedMultipliers
      )) {
        const threshold = parseInt(timeThreshold);
        if (this.gameTime >= threshold && this.lastSpeedUpdate < threshold) {
          this.applySpeedMultiplier(multiplier, threshold);
          this.lastSpeedUpdate = threshold;
          break;
        }
      }
    }
  }

  applySpeedMultiplier(multiplier, timeThreshold) {
    this.currentSpeedMultiplier = multiplier;

    // Send speed update event
    this.sendGameEvent("speed_update", {
      multiplier,
      timeThreshold,
      gameTime: this.gameTime,
    });

    // Play speed up sound effect if available
    this.sounds?.speedUpSound?.play();
  }

  handleTimeUp() {

    if (this.gameSettings.winCondition === "highest_score") {
      // In score-based modes, end the game when time is up
      this.gameOver();
    } else if (this.gameSettings.winCondition === "last_survivor") {
      // In survival modes, players continue until they die naturally
      // Just send a time up notification
      this.sendGameEvent("time_up", {
        gameTime: this.gameTime,
        finalScore: this.player.score,
      });
    }
  }

  stop() {
    this.isRunning = false;
    if (this.gameLoopId) {
      cancelAnimationFrame(this.gameLoopId);
      this.gameLoopId = null;
    }
    // Cancel any running laser beam animation
    if (this.laserAnimationId) {
      cancelAnimationFrame(this.laserAnimationId);
      this.laserAnimationId = null;
    }
    this.isGameStarted = false;
  }

  pause() {
    this.isPaused = true;
  }

  resume() {
    this.isPaused = false;
    this.lastDropTime = Date.now(); // Reset drop timer
  }

  handleInput(action) {

    if (!this.isGameStarted || this.isPaused || this.player.isGameOver || this.player.destroyInAction) {
      return;
    }

    // Normalize action for consistency
    const normalizedAction = this.normalizeAction(action);

    // Generate unique input ID to prevent double processing
    const inputId = `${this.playerId}_${++this.inputSequenceId}_${Date.now()}`;

    // Special handling for life saver - check if it can be used before sending to opponents
    if (normalizedAction === "lifeSaver") {
      // Check if life saver can be used without actually using it
      if (!this.player.lifeSaverCount || this.player.destroyInAction || this.player.isGameOver) {
        return; // Don't send any events if life saver can't be used
      }

      // Check if there are blocks to clear
      let hasBlocksToClear = false;
      for (let row = 0; row < this.boardRows; row++) {
        if (this.player.boardMatrix[row].some(cell => cell !== 0)) {
          hasBlocksToClear = true;
          break;
        }
      }

      if (!hasBlocksToClear) {
        return; // Don't send events if no blocks to clear
      }
    }

    // Send input event to other players (new system)
    this.sendInputEvent(normalizedAction, inputId);

    // Process input locally (current system - will be kept for now)
    this.processInput(normalizedAction, inputId, true); // true = isLocal
  }

  // New method: Send lightweight input events
  sendInputEvent(action, inputId, additionalData = null) {
    // Action is already normalized by handleInput
    const eventData = {
      action: action,
      inputId: inputId,
      timestamp: Date.now(),
      playerId: this.playerId
    };
    
    // Add additional data if provided (for piece spawn events)
    if (additionalData) {
      eventData.data = additionalData;
    }
    
    this.sendGameEvent("player_input", eventData);
  }

  // New method: Normalize different action names to consistent format
  normalizeAction(action) {
    const actionMap = {
      "ArrowLeft": "left",
      "ArrowRight": "right", 
      "ArrowDown": "down",
      "ArrowUp": "rotate",
      " ": "hard_drop",
      "hardDrop": "hard_drop",
      "left": "left",
      "right": "right",
      "down": "down", 
      "rotate": "rotate",
      "hard_drop": "hard_drop",
      "gravity_down": "gravity_down",
      "piece_spawn": "piece_spawn"
    };
    return actionMap[action] || action;
  }

  // New method: Process input (local or from network)
  processInput(action, inputId, isLocal = false) {
    
    // Prevent double processing of the same input
    if (this.processedInputIds.has(inputId)) {
      return;
    }
    this.processedInputIds.add(inputId);

    // Keep processed IDs list manageable (last 100 inputs)
    if (this.processedInputIds.size > 100) {
      const idsArray = Array.from(this.processedInputIds);
      this.processedInputIds.clear();
      idsArray.slice(-50).forEach(id => this.processedInputIds.add(id));
    }

    // Process the actual input (same logic as before)
    switch (action) {
      case "left":
        this.movePiece("left");
        break;
      case "right":
        this.movePiece("right");
        break;
      case "down":
        this.movePiece("down");
        break;
      case "gravity_down":
        this.movePiece("down", false); // false = automatic gravity, no sound
        break;
      case "rotate":
        this.rotatePiece();
        break;
      case "hard_drop":
        this.hardDrop();
        break;
      case "piece_spawn":
        // Piece spawn is handled locally - opponents will get this event and sync their simulators
        break;
      case "lifeSaver":
        return this.useLifeSaver();
    }
  }

  movePiece(direction, isManual = true) {
    if (!this.player.currentTetromino) return;

    const newTetromino = deepCloneArray(this.player.currentTetromino);

    switch (direction) {
      case "left":
        newTetromino.allCoordinates.forEach((coord) => coord[1]--);
        break;
      case "right":
        newTetromino.allCoordinates.forEach((coord) => coord[1]++);
        break;
      case "down":
        newTetromino.allCoordinates.forEach((coord) => coord[0]++);
        break;
    }

    if (!this.checkCollision(newTetromino)) {
      this.player.currentTetromino = newTetromino;
      this.updateHardDropPreview();
      // Removed old sendPieceUpdate() - new system uses input events
      
      // Play movement sound only for manual movements (only for themed boards)
      if (isManual && this.sounds) {
        const sound = this.themeSounds?.getSound('fall');
        if (sound && !sound.playing()) {
          sound.play();
        }
      }
      
      // Removed frequent movement logging to prevent spam
    } else if (direction === "down") {
      // Piece has landed - debug why this happens
      const currentBottomRow = Math.max(
        ...this.player.currentTetromino.allCoordinates.map((c) => c[0])
      );
      // Throttled collision logging
      if (Date.now() - this.lastCollisionLogTime > 2000) { // Log max once per 2 seconds
        this.lastCollisionLogTime = Date.now();
      }
      this.placePiece();
    }
  }

  rotatePiece() {
    if (!this.player.currentTetromino) return;

    const newTetromino = deepCloneArray(this.player.currentTetromino);
    const center = newTetromino.allCoordinates[0]; // Use first piece as rotation center

    // Rotate around center
    newTetromino.allCoordinates.forEach((coord) => {
      const x = coord[0] - center[0];
      const y = coord[1] - center[1];
      coord[0] = center[0] - y;
      coord[1] = center[1] + x;
    });

    if (!this.checkCollision(newTetromino)) {
      this.player.currentTetromino = newTetromino;
      this.updateHardDropPreview();
      // Removed old sendPieceUpdate() - new system uses input events
      
      // Play successful rotation sound (only for themed boards)
      if (this.sounds) {
        const sound = this.themeSounds?.getSound('fall');
        if (sound && !sound.playing()) {
          sound.play();
        }
      }
    } else {
      // Play failed rotation sound
      if (this.sounds) {
        const rotateSound = this.themeSounds?.getSound('rotateBlockSound');
        if (rotateSound && !rotateSound.playing()) {
          rotateSound.play();
        }
      }
    }
  }

  hardDrop() {

    if (!this.player.currentTetromino) {
      return;
    }

    if (!this.player.hardDropPreview) {
      // Try to calculate preview if it doesn't exist
      this.updateHardDropPreview();
      if (!this.player.hardDropPreview) {
        return;
      }
    }

    // Calculate drop distance for effect
    const startY = Math.min(...this.player.currentTetromino.allCoordinates.map(c => c[0]));
    const endY = Math.min(...this.player.hardDropPreview.allCoordinates.map(c => c[0]));
    const columns = [...new Set(this.player.currentTetromino.allCoordinates.map(c => c[1]))];

    // Emit hard drop effect event for 3D skin
    this.emitEffect('hardDrop', {
      startY: 20 - startY,
      endY: 20 - endY,
      columns,
      colorClass: this.player.currentTetromino.colorClass
    });

    // Play hard drop sound (only for themed boards)
    this.themeSounds?.getSound("whipSound")?.play();

    // Move piece to hard drop position using the preview coordinates
    this.player.currentTetromino = {
      ...this.player.currentTetromino,
      allCoordinates: deepCloneArray(this.player.hardDropPreview.allCoordinates)
    };


    // Clear the hard drop preview since piece is now placed
    this.player.hardDropPreview = null;
    this.player.hardDropCoordinates = null;

    // Place the piece normally using the standard placePiece method
    this.placePiece();
  }

  updateHardDropPreview() {
    if (!this.player.currentTetromino) {
      this.player.hardDropCoordinates = null;
      this.player.hardDropPreview = null;
      return;
    }

    // Calculate hard drop coordinates (same logic as utilities.js)
    let currentTetromino = deepCloneArray(this.player.currentTetromino);
    let flag = false;

    // Keep dropping until collision
    while (!this.checkCollision(currentTetromino)) {
      currentTetromino.allCoordinates.forEach((coord) => coord[0]++);
      flag = true;
    }

    if (flag) {
      // Move back one step to last valid position
      currentTetromino.allCoordinates.forEach((coord) => coord[0]--);
      
      // Store hard drop coordinates for reference (keep for compatibility)
      this.player.hardDropCoordinates = deepCloneArray(currentTetromino);
      
      // Create preview with ghost piece styling - this is what hardDrop() will use
      this.player.hardDropPreview = {
        allCoordinates: deepCloneArray(currentTetromino.allCoordinates),
        colorClass: "hard-drop-active", // Use the white border styling like local player
      };
      
    } else {
      this.player.hardDropCoordinates = null;
      this.player.hardDropPreview = null;
    }
  }

  checkCollision(tetromino) {
    if (!tetromino) return true;

    return tetromino.allCoordinates.some(([row, col]) => {
      // Debug: Log collision checks for pieces near bottom
      if (row >= this.boardRows - 2) {
      }

      // Check boundaries - standard tetris bounds
      if (
        row < 0 ||
        row >= this.boardRows ||
        col < 0 ||
        col >= this.boardColumns
      ) {
        return true;
      }

      // Check collision with placed pieces only for valid board positions
      const hasCollision =
        this.player.boardMatrix[row] && this.player.boardMatrix[row][col] !== 0;
      return hasCollision;
    });
  }

  placePiece() {
    if (!this.player.currentTetromino) return;

    // Calculate center position for block place effect
    const coords = this.player.currentTetromino.allCoordinates;
    const avgRow = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
    const avgCol = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;

    // Emit block place effect event for 3D skin
    this.emitEffect('blockPlace', {
      position: [avgCol - 5 + 0.5, 20 - avgRow - 0.5, 0],
      colorClass: this.player.currentTetromino.colorClass
    });

    // Place piece on board with color class for proper rendering
    this.player.currentTetromino.allCoordinates.forEach(([row, col]) => {
      if (
        row >= 0 &&
        row < this.boardRows &&
        col >= 0 &&
        col < this.boardColumns
      ) {
        // Store the color class so filled blocks render with correct colors
        this.player.boardMatrix[row][col] =
          this.player.currentTetromino.colorClass;
      }
    });

    // Increment board version to trigger React re-renders
    this.player.boardVersion++;

    // Removed old piece_placed event - new system handles this via input events
    // When piece lands from "down" input, opponents simulate the placement locally

    // Check for completed lines
    const completedLines = this.checkCompletedLines();
    if (completedLines.length > 0) {
      this.clearLines(completedLines);
      this.sounds?.clearSound?.play();
    }

    // Update stats
    this.player.stats.piecesPlaced++;

    // Spawn next piece
    this.spawnNextTetromino();

    this.sounds?.fallingHitSound?.play();
  }

  checkCompletedLines() {
    const completedLines = [];

    for (let row = 0; row < this.boardRows; row++) {
      if (this.player.boardMatrix[row].every((cell) => cell !== 0)) {
        completedLines.push(row);
      }
    }

    return completedLines;
  }

  clearLines(lines) {
    if (lines.length > 0) {
      // Capture block colors before clearing for 3D effect
      const blocksData = this.captureBlocksFromRows(lines);

      // Emit line clear effect event for 3D skin
      if (lines.length >= 2) {
        // 2+ lines - Emit special finisher effect with block data (lowered from 4 for testing)
        this.emitEffect('tetris', { rows: lines, linesCleared: lines.length, blocks: blocksData });
      } else {
        this.emitEffect('lineClear', { rows: lines, linesCleared: lines.length, blocks: blocksData });
      }

      // Emit score animation effect for 3D rendering (skip for default board)
      if (!this.themeSounds?.currentThemeConfig?.disableScoreAnimation) {
        this.emitEffect('scoreAnimation', {
          rows: lines.length,
          score: lines.length * 100 * lines.length,
          colorClass: blocksData[0]?.colorClass,
        });
      }

      // Start laser beam animation before clearing lines
      this.startLaserBeamAnimation(lines);
    }
  }

  // Capture block positions and colors from rows for 3D effects
  captureBlocksFromRows(rows) {
    const blocks = [];
    rows.forEach(row => {
      for (let col = 0; col < this.boardColumns; col++) {
        const cell = this.player.boardMatrix[row][col];
        if (cell && cell !== 0) {
          blocks.push({
            row,
            col,
            colorClass: cell // The CSS class like 'l-tetromino-active'
          });
        }
      }
    });
    return blocks;
  }

  startLaserBeamAnimation(destroyableRows) {
    let column = 0;
    const msPerStep = 22.33; // Milliseconds per step (3x speed - 67/3)
    let lastStepTime = performance.now();
    this.player.destroyInAction = true;

    // Play different sounds based on number of lines cleared
    if (destroyableRows.length >= 2) {
      // Multi-line clear (2+) - play finisher sound for 3D themes, blaster for default
      const finisherSound = this.themeSounds?.getSound('deliciousSound') || this.sounds?.blasterSound;
      finisherSound?.play();
    } else {
      // Single line clear - play regular line clear sound
      const lineClearSound = this.themeSounds?.getSound('blasterSound') || this.sounds?.blasterSound;
      lineClearSound?.play();
    }

    const laserAnimationLoop = (currentTime) => {
      // Check if game is still running before continuing animation
      if (!this.isRunning || !this.isGameStarted) {
        this.laserAnimationId = null;
        return;
      }

      const elapsed = currentTime - lastStepTime;

      if (elapsed >= msPerStep) {
        if (column >= this.boardColumns) {
          // Animation complete - finish clearing lines
          this.laserAnimationId = null;
          this.finishLineClear(destroyableRows);
          return;
        }

        // Update laser beam visual effect (if DOM elements exist)
        destroyableRows.forEach((row) => {
          this.player.boardMatrix[row][column] = "";

          // Update laser beam width if DOM element exists
          const laserBeam = document.querySelector(
            `#online-player-laser-beam-${row}`
          );
          if (laserBeam) {
            // Show and animate the laser beam
            laserBeam.style.width = (26 * (column + 1)) + "px";
          } else {
          }
        });

        // Increment board version to update 3D visuals during laser animation
        this.player.boardVersion++;

        column++;
        lastStepTime = currentTime;

        // Removed old laser_animation event - new system handles line clears locally
        // Opponents will process the "down" input, detect line clears, and run their own laser animations
      }

      this.laserAnimationId = requestAnimationFrame(laserAnimationLoop);
    };

    this.laserAnimationId = requestAnimationFrame(laserAnimationLoop);
  }

  // Life saver laser animation (similar to line clear but different completion)
  startLifeSaverAnimation(destroyableRows) {
    // Capture block colors before clearing for 3D effect
    const blocksData = this.captureBlocksFromRows(destroyableRows);

    // Emit lineClear effect for 3D skin (life saver fires line clear animation, not finisher)
    this.emitEffect('lineClear', { rows: destroyableRows, linesCleared: destroyableRows.length, blocks: blocksData, isLifeSaver: true });

    let column = 0;
    const msPerStep = 22.33; // Milliseconds per step (3x speed - 67/3)
    let lastStepTime = performance.now();
    this.player.destroyInAction = true;

    // Play line clear sound for life saver
    const lineClearSound = this.themeSounds?.getSound('blasterSound') || this.sounds?.blasterSound;
    lineClearSound?.play();

    const laserAnimationLoop = (currentTime) => {
      // Check if game is still running before continuing animation
      if (!this.isRunning || !this.isGameStarted) {
        this.laserAnimationId = null;
        return;
      }

      const elapsed = currentTime - lastStepTime;

      if (elapsed >= msPerStep) {
        if (column >= this.boardColumns) {
          // Animation complete - finish life saver operation
          this.laserAnimationId = null;
          this.finishLifeSaver(destroyableRows);
          return;
        }

        // Update laser beam visual effect (if DOM elements exist)
        destroyableRows.forEach((row) => {
          this.player.boardMatrix[row][column] = "";

          // Update laser beam width if DOM element exists
          const laserBeam = document.querySelector(
            `#online-player-laser-beam-${row}`
          );
          if (laserBeam) {
            // Show and animate the laser beam
            laserBeam.style.width = (26 * (column + 1)) + "px";
          } else {
          }
        });

        // Increment board version to update 3D visuals during life saver laser animation
        this.player.boardVersion++;

        column++;
        lastStepTime = currentTime;
      }

      this.laserAnimationId = requestAnimationFrame(laserAnimationLoop);
    };

    this.laserAnimationId = requestAnimationFrame(laserAnimationLoop);
  }

  finishLineClear(destroyableRows) {
    // Reset laser beams
    destroyableRows.forEach((row) => {
      const laserBeam = document.querySelector(
        `#online-player-laser-beam-${row}`
      );
      if (laserBeam) {
        // Hide the laser beam and reset width
        laserBeam.style.width = "0px";
      } else {
      }
    });

    // Remove completed lines
    destroyableRows.forEach((lineIndex) => {
      this.player.boardMatrix.splice(lineIndex, 1);
      // Add new empty line at top
      this.player.boardMatrix.unshift(new Array(this.boardColumns).fill(0));
    });

    // Increment board version to update 3D visuals after line shift
    this.player.boardVersion++;

    // Update score and stats
    const lineCount = destroyableRows.length;
    this.player.lines += lineCount;

    // Track line clearing breakdown
    switch (lineCount) {
      case 1:
        this.player.stats.singleLines++;
        break;
      case 2:
        this.player.stats.doubleLines++;
        break;
      case 3:
        this.player.stats.tripleLines++;
        break;
      case 4:
        this.player.stats.tetrisLines++;
        break;
    }

    // Calculate score based on lines cleared
    const scoreMultiplier = [0, 100, 400, 900, 1600][lineCount] || 0;
    this.player.score += scoreMultiplier * this.player.level;

    // Level progression disabled - keep at level 1 for consistent scoring
    // const newLevel = Math.floor(this.player.lines / 10) + 1;
    // if (newLevel > this.player.level) {
    //   this.player.level = newLevel;
    //   // Increase speed
    //   this.dropInterval = Math.max(50, 1000 - (this.player.level - 1) * 50);
    // }

    // End destroy action
    this.player.destroyInAction = false;

    // Send score update event for speed battle mode ending logic
    this.sendGameEvent("score_update", {
      score: this.player.score,
      level: this.player.level,
      lines: this.player.lines,
      singleLines: this.player.stats.singleLines,
      doubleLines: this.player.stats.doubleLines,
      tripleLines: this.player.stats.tripleLines,
      tetrisLines: this.player.stats.tetrisLines,
    });

    // Removed old lines_cleared event - new system handles line clears locally
    // Opponents process the "down" input that caused the line clear and simulate it locally
  }

  finishLifeSaver(destroyableRows) {
    // Reset laser beams
    destroyableRows.forEach((row) => {
      const laserBeam = document.querySelector(
        `#online-player-laser-beam-${row}`
      );
      if (laserBeam) {
        // Hide the laser beam and reset width
        laserBeam.style.width = "0px";
      }
    });

    // Clear the rows (set all cells to 0)
    destroyableRows.forEach(row => {
      for (let col = 0; col < this.boardColumns; col++) {
        this.player.boardMatrix[row][col] = 0;
      }
    });

    // Shift blocks down using existing method
    this.shiftBlocksDown(destroyableRows);

    // Increment board version to trigger React re-renders
    this.player.boardVersion++;

    // End destroy action
    this.player.destroyInAction = false;

    // Trigger UI update
    if (this.onStateUpdate) {
      this.onStateUpdate();
    }

  }

  gameOver() {
    this.player.isGameOver = true;
    // DON'T stop the game - keep running to track other players for match results
    
    this.sendGameEvent("game_over", {
      finalScore: this.player.score,
      level: this.player.level,
      lines: this.player.lines,
      singleLines: this.player.stats.singleLines,
      doubleLines: this.player.stats.doubleLines,
      tripleLines: this.player.stats.tripleLines,
      tetrisLines: this.player.stats.tetrisLines,
    });

    this.sounds?.gameOverSound?.play();
  }

  useLifeSaver() {
    if (!this.player.lifeSaverCount || this.player.destroyInAction || this.player.isGameOver) {
      return false;
    }

    // Find first non-empty row from top
    let firstNonEmptyRow = -1;
    for (let row = 0; row < this.boardRows; row++) {
      // Check if any cell in this row is filled
      if (this.player.boardMatrix[row].some(cell => cell !== 0)) {
        firstNonEmptyRow = row;
        break;
      }
    }

    if (firstNonEmptyRow !== -1) {
      // Create array of rows to destroy (top 5 rows starting from first non-empty)
      const destroyableRows = [firstNonEmptyRow];
      let nextRow = firstNonEmptyRow + 1;
      while (destroyableRows.length < 5 && nextRow < this.boardRows) {
        destroyableRows.push(nextRow);
        nextRow++;
      }

      // Decrement life saver count
      this.player.lifeSaverCount--;

      // Send life saver event to other players immediately for responsive UI
      this.sendGameEvent("life_saver_used", {
        playerId: this.player.id,
        rowsCleared: destroyableRows.length,
        remainingLifeSavers: this.player.lifeSaverCount
      });

      // Start laser animation for life saver (different from line clearing)
      this.startLifeSaverAnimation(destroyableRows);
      return true;
    }

    return false; // No blocks to clear
  }

  shiftBlocksDown(destroyableRows) {
    // Sort rows in descending order to process from bottom to top
    const sortedRows = [...destroyableRows].sort((a, b) => b - a);

    sortedRows.forEach(row => {
      // Move all rows above this one down by one position
      for (let r = row; r > 0; r--) {
        for (let col = 0; col < this.boardColumns; col++) {
          this.player.boardMatrix[r][col] = this.player.boardMatrix[r - 1][col];
        }
      }

      // Clear the top row
      for (let col = 0; col < this.boardColumns; col++) {
        this.player.boardMatrix[0][col] = 0;
      }
    });
  }

  updateOpponent(opponentId, gameData) {
    // Merge with existing opponent data
    const existingData = this.opponents.get(opponentId) || { id: opponentId, lifeSaverCount: 3 };
    const updatedData = {
      ...existingData,
      ...gameData,
      lastUpdate: Date.now(),
    };
    this.opponents.set(opponentId, updatedData);

    // Also sync with simulator if it exists (for network validation)
    const simulator = this.opponentSimulators.get(opponentId);
    if (simulator) {
      simulator.syncFromNetwork(gameData);
    }
  }

  removeOpponent(opponentId) {
    this.opponents.delete(opponentId);
    // Clean up simulator before deleting
    const simulator = this.opponentSimulators.get(opponentId);
    if (simulator) {
      simulator.cleanup();
    }
    this.opponentSimulators.delete(opponentId);
  }

  // New method: Handle input events from other players
  handleOpponentInput(opponentId, action, inputId, timestamp, additionalData = null) {
    
    // Get or create opponent simulator
    let simulator = this.opponentSimulators.get(opponentId);
    if (!simulator) {
      // Create callback to trigger UI updates during animations
      const onStateUpdate = (updatedOpponentId, updatedState) => {
        // Update the opponents map to trigger UI re-render
        const currentOpponent = this.opponents.get(updatedOpponentId) || {};
        this.opponents.set(updatedOpponentId, {
          ...currentOpponent,
          ...updatedState
        });

        // Notify React component to re-render
        if (this.onOpponentStateChange) {
          this.onOpponentStateChange(updatedOpponentId);
        }
      };

      simulator = new OpponentSimulator(opponentId, this.boardRows, this.boardColumns, onStateUpdate);
      this.opponentSimulators.set(opponentId, simulator);
      
      // Initialize simulator with any existing opponent data
      const existingOpponentData = this.opponents.get(opponentId);
      if (existingOpponentData) {
        simulator.syncFromNetwork(existingOpponentData);
      }
      
    }
    
    // Handle piece spawn events with provided data
    let success;
    if (action === "piece_spawn" && additionalData) {
      
      // Set the new pieces directly from the provided data
      if (additionalData.currentPiece) {
        simulator.state.currentTetromino = additionalData.currentPiece;
      }
      
      if (additionalData.nextPiece) {
        simulator.state.nextTetromino = additionalData.nextPiece;
      } else {
        simulator.state.nextTetromino = null;
      }
      
      simulator.updateHardDropPreview();
      success = true; // Piece spawn is always successful
    } else {
      // Process input locally for instant visual feedback
      success = simulator.processInput(action, inputId, timestamp);
    }
    
    if (success) {
      // Update opponents map with simulator state for rendering
      const simulatedState = simulator.getState();
      this.updateOpponent(opponentId, {
        board: simulatedState.boardMatrix,
        boardVersion: simulatedState.boardVersion,
        currentPiece: simulatedState.currentTetromino,
        nextPiece: simulatedState.nextTetromino,
        hardDropPreview: simulatedState.hardDropPreview,
        score: simulatedState.score,
        level: simulatedState.level,
        lines: simulatedState.lines,
        singleLines: simulatedState.singleLines,
        doubleLines: simulatedState.doubleLines,
        tripleLines: simulatedState.tripleLines,
        tetrisLines: simulatedState.tetrisLines,
        isGameOver: simulatedState.isGameOver,
        lastUpdate: simulatedState.lastUpdate
      });
      
      return true;
    } else {
      return false;
    }
  }

  // New method: Send periodic state sync for validation
  sendStateSync() {
    if (!this.isGameStarted || this.player.isGameOver) return;

    const stateData = {
      board: this.player.boardMatrix,
      score: this.player.score,
      level: this.player.level,
      lines: this.player.lines,
      singleLines: this.player.stats.singleLines,
      doubleLines: this.player.stats.doubleLines,
      tripleLines: this.player.stats.tripleLines,
      tetrisLines: this.player.stats.tetrisLines,
      currentPiece: this.player.currentTetromino,
      nextPiece: this.player.nextTetromino,
      lifeSaverCount: this.player.lifeSaverCount,
      gameTime: this.gameTime,
      timestamp: Date.now()
    };

    // Create a simple hash for quick comparison
    const stateHash = this.createStateHash(stateData);
    
    
    this.sendGameEvent("state_sync", {
      ...stateData,
      stateHash: stateHash
    });
  }

  // New method: Handle incoming state sync from opponents
  handleOpponentStateSync(opponentId, syncData) {
    const simulator = this.opponentSimulators.get(opponentId);
    if (!simulator) {
      // Create callback to trigger UI updates during animations
      const onStateUpdate = (updatedOpponentId, updatedState) => {
        // Update the opponents map to trigger UI re-render
        const currentOpponent = this.opponents.get(updatedOpponentId) || {};
        this.opponents.set(updatedOpponentId, {
          ...currentOpponent,
          ...updatedState
        });

        // Notify React component to re-render
        if (this.onOpponentStateChange) {
          this.onOpponentStateChange(updatedOpponentId);
        }
      };

      const newSimulator = new OpponentSimulator(opponentId, this.boardRows, this.boardColumns, onStateUpdate);
      this.opponentSimulators.set(opponentId, newSimulator);
      newSimulator.syncFromNetwork(syncData);
      return;
    }

    // Compare current simulator state with network state
    const currentState = simulator.getState();
    const hasDesync = this.detectDesync(currentState, syncData);
    
    if (hasDesync) {

      // Correct the desync by accepting network state as authoritative
      simulator.syncFromNetwork(syncData);

      // Get updated state after sync (includes incremented boardVersion)
      const updatedState = simulator.getState();

      // Update opponent rendering data
      this.updateOpponent(opponentId, {
        board: updatedState.boardMatrix,
        boardVersion: updatedState.boardVersion,
        currentPiece: syncData.currentPiece,
        nextPiece: syncData.nextPiece,
        score: syncData.score,
        level: syncData.level,
        lines: syncData.lines,
        singleLines: syncData.singleLines,
        doubleLines: syncData.doubleLines,
        tripleLines: syncData.tripleLines,
        tetrisLines: syncData.tetrisLines,
        lifeSaverCount: syncData.lifeSaverCount,
        lastUpdate: Date.now()
      });

    } else {
      // Still sync for piece updates and lifeSaverCount even if hash matches
      simulator.syncFromNetwork({
        currentPiece: syncData.currentPiece,
        nextPiece: syncData.nextPiece
      });
      if (syncData.lifeSaverCount !== undefined) {
        this.updateOpponent(opponentId, { lifeSaverCount: syncData.lifeSaverCount });
      }
    }
  }

  // New method: Detect if local simulation has desynced from network
  detectDesync(localState, networkState) {
    // Compare critical game state elements
    const scoreDiff = Math.abs((localState.score || 0) - (networkState.score || 0));
    const linesDiff = Math.abs((localState.lines || 0) - (networkState.lines || 0));
    const levelDiff = Math.abs((localState.level || 1) - (networkState.level || 1));

    // Allow small score differences (up to 100 points) due to timing differences
    const significantScoreDiff = scoreDiff > 100;
    const significantLinesDiff = linesDiff > 0; // Lines should match exactly
    const significantLevelDiff = levelDiff > 0; // Levels should match exactly

    // Also compare board state hash if available
    let boardDesync = false;
    if (networkState.stateHash) {
      const localHash = this.createStateHash({
        score: localState.score,
        lines: localState.lines,
        level: localState.level,
        board: localState.boardMatrix
      });
      boardDesync = localHash !== networkState.stateHash;
    }

    const hasDesync = significantScoreDiff || significantLinesDiff || significantLevelDiff || boardDesync;

    if (hasDesync) {
    }

    return hasDesync;
  }

  // New method: Create simple hash of game state
  createStateHash(stateData) {
    // Simple hash based on critical state elements
    const criticalState = {
      score: stateData.score,
      lines: stateData.lines, 
      level: stateData.level,
      boardSummary: this.getBoardSummary(stateData.board)
    };
    
    // Convert to string and create simple hash
    const stateString = JSON.stringify(criticalState);
    let hash = 0;
    for (let i = 0; i < stateString.length; i++) {
      const char = stateString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }

  // New method: Get summary of board state for hashing
  getBoardSummary(board) {
    let summary = '';
    for (let row = 0; row < board.length; row++) {
      let rowSummary = '';
      for (let col = 0; col < board[row].length; col++) {
        rowSummary += board[row][col] ? '1' : '0';
      }
      // Only include rows that have any filled cells
      if (rowSummary.includes('1')) {
        summary += `${row}:${rowSummary}|`;
      }
    }
    return summary;
  }

  sendGameEvent(eventType, eventData) {
    if (!this.onGameEvent) return;

    // Don't send events if the game is over (except the game_over event itself)
    if (this.player.isGameOver && eventType !== "game_over") {
      return;
    }

    try {
      // Log critical events for debugging
      if (eventType === "hard_drop_complete") {
      }
      
      this.onGameEvent(eventType, eventData);
    } catch (error) {
      // Don't throw the error to prevent game crashes
    }
  }

  // Removed sendPieceUpdate() method - no longer needed with input-based system

  // Getters for external access
  getPlayer() {
    return { ...this.player };
  }

  getOpponents() {
    return Array.from(this.opponents.values());
  }

  getBoardMatrix() {
    return this.player.boardMatrix;
  }

  getBoardVersion() {
    return this.player.boardVersion;
  }

  getCurrentTetromino() {
    return this.player.currentTetromino;
  }

  getHardDropCoordinates() {
    return this.player.hardDropCoordinates;
  }

  getHardDropPreview() {
    return this.player.hardDropPreview;
  }

  getGameStats() {
    return {
      score: this.player.score,
      level: this.player.level,
      linesCleared: this.player.lines,
      gameTime: this.gameTime,
      piecesPlaced: this.player.stats.piecesPlaced,
      isGameOver: this.player.isGameOver,
      gameMode: this.gameSettings.gameMode,
      timeLimit: this.gameSettings.timeLimit,
      isTimeUp: this.isTimeUp,
      currentSpeedMultiplier: this.currentSpeedMultiplier,
      singleLines: this.player.stats.singleLines,
      doubleLines: this.player.stats.doubleLines,
      tripleLines: this.player.stats.tripleLines,
      tetrisLines: this.player.stats.tetrisLines,
    };
  }
}
